/**
 * M3 component behaviors, ported from allmoxy-design-system components/behaviors.js
 * (DEV-12298). Vanilla JS — no Prototype/jQuery.
 *
 * Tooltip placement (every .am-tooltip: viewport-fixed tips, above/below flip, clamp,
 * reposition-while-visible — canon tooltip.css ruling 2026-07-28) plus the chip
 * truncation tooltip: a chip whose label is clipped to an ellipsis reveals its full
 * label on hover via the certified tooltip (.am-tooltip / .am-tooltip__tip,
 * m3/_tooltip.scss). Covers the canon chips (.am-chip > .am-chip__label) and the
 * legacy tag widget: fcbkcomplete input chips (ul.holder li.bit-box, whose caption
 * is a bare text node until we wrap it) and the "More Tags" cloud (ul.cloud li span).
 */
// The leading semicolon is load-bearing: this file is concatenated into the js bundles,
// and if the previous file ends in an unterminated expression, a bare ( would turn it
// into a call of that expression (the links.js "(intermediate value) is not a function"
// breakage, DEV-12298 follow-up).
;(function () {
	'use strict';

	var CHIP_SELECTOR = '.am-chip, ul.holder li.bit-box, ul.cloud li';
	var LABEL_CLASS = 'am-chip__label';

	function decorateChip(lbl, chip, clipped) { /* DEV-12855: chip/clipped may arrive pre-measured (flushChipQueue) */
		chip = chip || lbl.closest(CHIP_SELECTOR);
		if (!chip) return;
		if (clipped === undefined) clipped = lbl.scrollWidth > lbl.clientWidth + 1; /* +1 guards sub-pixel rounding */
		var tip = chip.querySelector('.am-chip__tip');
		if (clipped) {
			chip.classList.add('am-tooltip');
			if (!tip) {
				tip = document.createElement('span');
				tip.className = 'am-tooltip__tip am-chip__tip';
				chip.appendChild(tip);
			}
			tip.textContent = lbl.textContent.trim();
			/* DEV-12855: no placeTip here - it reads layout mid-write and turned a bulk
			   decoration pass into forced-layout thrash. showTip places on every hover and
			   focus (capture listeners below), which is also fresher than a parse-time spot. */
			/* the cloud span carries a native title ("name (qty)"); park it while the
			   certified tip is active so the browser tooltip doesn't double up */
			if (lbl.hasAttribute('title')) {
				lbl.setAttribute('data-am-title', lbl.getAttribute('title'));
				lbl.removeAttribute('title');
			}
		} else if (tip) {
			chip.classList.remove('am-tooltip');
			tip.remove();
			if (lbl.hasAttribute('data-am-title')) {
				lbl.setAttribute('title', lbl.getAttribute('data-am-title'));
				lbl.removeAttribute('data-am-title');
			}
		}
	}

	/* Tooltip placement, every .am-tooltip (canon behaviors.js port, ruled 2026-07-28;
	   generalized from the chip-tip special case that proved it here first). The tip is
	   position:fixed (m3/_tooltip.scss) so no ancestor overflow can clip it, which means
	   it has no natural anchor: this places it. On hover or focus, measure the trigger
	   and put the tip centred above it, flipped below (data-am-tip-place="below") when
	   there is no room above, and clamped so it never runs off screen. Reveal stays
	   CSS-owned (:hover/:focus-within opacity) — this only computes coordinates.
	   Delegated at parse time, so tips added later are placed too. App delta vs canon:
	   offsetWidth/Height instead of getBoundingClientRect for the tip's size — layout
	   metrics are immune to the resting scale(.9) transform. */
	var TIP_GAP = 8; /* spacing-xs, canon behaviors.js GAP */
	var openTip = null;

	function fixedOrigin(el) {
		for (var n = el.parentElement; n && n !== document.documentElement; n = n.parentElement) {
			var s = getComputedStyle(n);
			if ((s.transform && s.transform !== 'none') ||
				(s.perspective && s.perspective !== 'none') ||
				(s.filter && s.filter !== 'none') ||
				(s.backdropFilter && s.backdropFilter !== 'none') ||
				(s.willChange && s.willChange.indexOf('transform') !== -1) ||
				(s.contain && /paint|layout|strict|content/.test(s.contain))) {
				return n.getBoundingClientRect();
			}
		}
		return null;
	}

	function placeTip(anchorEl) {
		var tip = anchorEl.querySelector('.am-tooltip__tip');
		if (!tip) return;
		var w = tip.offsetWidth, h = tip.offsetHeight;
		if (!w) return;
		var a = anchorEl.getBoundingClientRect();
		var origin = fixedOrigin(tip);
		var above = a.top - TIP_GAP - h >= 0;
		if (anchorEl.hasAttribute('data-am-tip-below') && a.bottom + TIP_GAP + h <= window.innerHeight) above = false;
		tip.setAttribute('data-am-tip-place', above ? 'above' : 'below');
		var pad = 4, half = w / 2;
		var x = a.left + a.width / 2;
		x = Math.max(pad + half, Math.min(x, window.innerWidth - pad - half));
		/* the CSS translate does the rest: above rides translate(-50%, -100%) so top is
		   the tip's bottom edge; below rides translate(-50%, 0) so top is its top edge */
		var y = above ? a.top - TIP_GAP : a.bottom + TIP_GAP;
		if (origin) { x -= origin.left; y -= origin.top; }
		tip.style.left = Math.round(x) + 'px';
		tip.style.top = Math.round(y) + 'px';
	}

	/* reposition on scroll/resize while a tip is visible; the listeners exist only
	   while one is (canon repositions too — this is the cheap form of it) */
	/* DEV-12855: rAF-throttled - the capture-phase scroll listener fires for every
	   scroller in the document, and placeTip is a forced layout; once per frame is
	   plenty. isConnected guards the anchor being ajax-replaced mid-hover, which left
	   the old code repositioning a detached node on every scroll event forever. */
	var tipRafQueued = false;
	function repositionOpenTip() {
		if (tipRafQueued) return;
		tipRafQueued = true;
		requestAnimationFrame(function () {
			tipRafQueued = false;
			if (!openTip) return;
			if (!openTip.isConnected) { openTip = null; return; }
			placeTip(openTip);
		});
	}
	function showTip(e) {
		var el = e.target.closest ? e.target.closest('.am-tooltip') : null;
		if (!el) return;
		if (!openTip) {
			window.addEventListener('scroll', repositionOpenTip, { capture: true, passive: true });
			window.addEventListener('resize', repositionOpenTip);
		}
		openTip = el;
		placeTip(el);
	}
	function hideTip(e) {
		var el = e.target.closest ? e.target.closest('.am-tooltip') : null;
		if (!el || el !== openTip) return;
		openTip = null;
		window.removeEventListener('scroll', repositionOpenTip, { capture: true, passive: true });
		window.removeEventListener('resize', repositionOpenTip);
	}
	document.addEventListener('pointerover', showTip, true);
	document.addEventListener('focusin', showTip, true);
	document.addEventListener('pointerout', hideTip, true);
	document.addEventListener('focusout', hideTip, true);

	/* fcbkcomplete renders the chip caption as a bare text node before the close link;
	   give it the canon label box so it can truncate and host the tooltip. The widget
	   serializes from cached data, not from this markup, so the wrap is inert to it. */
	function wrapBitBoxLabel(li) {
		if (li.querySelector('.' + LABEL_CLASS)) return li.querySelector('.' + LABEL_CLASS);
		var span = document.createElement('span');
		span.className = LABEL_CLASS;
		var node = li.firstChild, next;
		while (node) {
			next = node.nextSibling;
			if (node.nodeType === 3) span.appendChild(node); /* move text nodes into the label */
			else if (node.nodeType === 1 && node.tagName !== 'A') span.appendChild(node);
			node = next;
		}
		li.insertBefore(span, li.firstChild);
		return span;
	}

	function labelsIn(root) {
		var labels = [];
		if (root.nodeType !== 1) return labels;
		if (root.matches && root.matches('li.bit-box')) root = root.parentNode || root;
		root.querySelectorAll('ul.holder li.bit-box').forEach(function (li) {
			labels.push(wrapBitBoxLabel(li));
		});
		root.querySelectorAll('.' + LABEL_CLASS + ', ul.cloud li > span').forEach(function (lbl) {
			lbl.classList.add(LABEL_CLASS);
			if (labels.indexOf(lbl) === -1) labels.push(lbl);
		});
		return labels;
	}

	/* DEV-12855: a burst of deliveries (a tag cloud slideToggle resizes every chip in it,
	   every animation frame) used to run decorateChip per entry per frame - read/write
	   interleaved = thousands of forced full-document layouts = the customer's Chrome
	   'Page Unresponsive'. Coalesce all deliveries into one flush per frame, and inside
	   the flush do every measurement first (one layout), then every DOM write. */
	var chipPending = typeof Set !== 'undefined' ? new Set() : null;
	var chipFlushQueued = false;
	function flushChipQueue() {
		chipFlushQueued = false;
		var jobs = [];
		chipPending.forEach(function (lbl) {
			if (!lbl.isConnected) return;
			var chip = lbl.closest(CHIP_SELECTOR);
			if (!chip) return;
			jobs.push({ lbl: lbl, chip: chip, clipped: lbl.scrollWidth > lbl.clientWidth + 1 });
		});
		chipPending.clear();
		jobs.forEach(function (j) { decorateChip(j.lbl, j.chip, j.clipped); });
	}
	var chipRO = (window.ResizeObserver && chipPending) ? new ResizeObserver(function (entries) {
		entries.forEach(function (e) { chipPending.add(e.target); });
		if (!chipFlushQueued) {
			chipFlushQueued = true;
			requestAnimationFrame(flushChipQueue);
		}
	}) : null;

	var seen = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
	function watch(labels) {
		labels.forEach(function (lbl) {
			if (seen) {
				if (seen.has(lbl)) return;
				seen.add(lbl);
			}
			if (chipRO) chipRO.observe(lbl); /* fires immediately, decorates on visibility/size changes */
			else decorateChip(lbl);
		});
	}

	function init() {
		watch(labelsIn(document.body));
		/* place every static tip once at parse (canon does the same) so a tip revealed by
		   CSS hover before any pointer event has coordinates instead of sitting at 0,0 */
		document.querySelectorAll('.am-tooltip').forEach(placeTip);
		/* tag chips are added at runtime (fcbkcomplete picks, inventory tag editor re-renders) */
		if (window.MutationObserver) {
			new MutationObserver(function (mutations) {
				mutations.forEach(function (m) {
					for (var i = 0; i < m.addedNodes.length; i++) watch(labelsIn(m.addedNodes[i]));
				});
			}).observe(document.body, { childList: true, subtree: true });
		}
		if (!chipRO) {
			window.addEventListener('resize', function () {
				labelsIn(document.body).forEach(decorateChip);
			});
		}
	}

	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
	else init();
})();

/**
 * M3 chart tokens (DEV-12378): reads the canon chart palettes and type tokens off
 * :root so Google Charts options can carry token-derived values (GViz draws
 * inline-styled SVG and cannot consume CSS custom properties directly).
 * Palette routing per the reports coloring decision (locked 2026-07-02): div =
 * ranked / good-to-bad ramp (aging is the reference), cat = unordered categories
 * (color separates, never ranks), seq = single-measure magnitude.
 */
/**
 * M3 tabs behavior (DEV-12499, first consumer: employee home time card/tracking),
 * ported from allmoxy-design-system components/behaviors.js (tabs block). Vanilla JS.
 *
 * Contract: a strip `.am-tabs` holds `.am-tab[data-tab="<name>"]` triggers carrying
 * aria-selected; panels are `[data-panel="<name>"]` elements inside the nearest
 * `[data-tab-scope]` ancestor (falling back to document), toggled via the `hidden`
 * attribute. Gated on [data-tab] so Tabset.php's navigate-only anchor strips (no
 * data-tab) are untouched. App adaptation over canon: our triggers can be anchors
 * keeping their deep-link hrefs, so plain activations are preventDefault-ed —
 * middle-click/ctrl-click still navigate.
 */
;(function () {
	'use strict';

	/* Delegated at parse time, NOT bound per-tab on DOMContentLoaded: the triggers are
	   real anchors (deep links preserved), so any click landing in the window between
	   first paint and per-node wiring fell through to a full page navigation — QA hit
	   this racing the page load ("tabs do a full reload"). One document-level listener
	   exists before the strip can be clicked and survives re-rendered strips. Still
	   gated on [data-tab] so Tabset.php's navigate-only anchor strips are untouched;
	   modified clicks (middle/ctrl/cmd/shift) still navigate. */
	document.addEventListener('click', function (e) {
		if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
		var tab = e.target && e.target.closest ? e.target.closest('.am-tabs .am-tab[data-tab]') : null;
		if (!tab) return;
		e.preventDefault();
		var strip = tab.closest('.am-tabs');
		strip.querySelectorAll('.am-tab').forEach(function (t) { t.setAttribute('aria-selected', 'false'); });
		tab.setAttribute('aria-selected', 'true');
		var name = tab.getAttribute('data-tab');
		if (!name) return;
		var scope = strip.closest('[data-tab-scope]') || document;
		scope.querySelectorAll('[data-panel]').forEach(function (p) {
			p.hidden = (p.getAttribute('data-panel') !== name);
		});
	});
})();

;window.amChartTokens = function () {
	'use strict';
	var cs = getComputedStyle(document.documentElement);
	function read(name) { return cs.getPropertyValue(name).trim(); }
	function ramp(prefix, n) {
		var out = [];
		for (var i = 1; i <= n; i++) out.push(read(prefix + i));
		return out;
	}
	var font = (read('--md-sys-typescale-font').split(',')[0] || '').replace(/["']/g, '').trim() || 'Roboto';
	return {
		div: ramp('--am-chart-div-', 5),
		cat: ramp('--am-chart-cat-', 8),
		seq: ramp('--am-chart-seq-', 5),
		font: font,
		onSurface: read('--md-sys-color-on-surface'),
		onSurfaceVariant: read('--md-sys-color-on-surface-variant'),
		outlineVariant: read('--md-sys-color-outline-variant'),
		barRadius: parseFloat(read('--am-chart-bar-radius')) || 2
	};
};

/**
 * Checkbox-backed filter chips (DEV-12549): rail toggle filters render as canon
 * filter chips wrapping their REAL checkbox (label.am-chip > input[type=checkbox]).
 * The input must stay native so livetable's filter observers (scripts/site.js
 * install_filter_observers) receive the label's activation click; this behavior
 * only mirrors the checked state onto the chip's visual class. Parse-time
 * delegated listener — no wiring race with late-rendered rails.
 */
;(function () {
	'use strict';
	function syncChip(input) {
		var chip = input.closest ? input.closest('label.am-chip') : null;
		if (chip) chip.classList.toggle('am-chip--selected', input.checked);
	}
	document.addEventListener('change', function (e) {
		var input = e.target;
		if (!input || (input.type !== 'checkbox' && input.type !== 'radio')) return;
		syncChip(input);
		// Radio groups uncheck their previous member silently, and legacy helpers
		// (sync_cb_with_group) flip .checked programmatically with no events — resync
		// the whole name-group on the next tick so every chip's visual state is honest.
		if (input.name) {
			setTimeout(function () {
				document.querySelectorAll('label.am-chip > input[name="' + CSS.escape(input.name) + '"]').forEach(syncChip);
			}, 0);
		}
	});
})();

/**
 * Generic menu trigger (DEV-12549): a click on [data-am-menu-trigger] toggles .is-open
 * on its .am-menu-anchor (m3/_menu.scss owns surface + animation); any click outside
 * an open anchor closes it. Parse-time delegated — fills the "data-menu-trigger into
 * the void" hole the canon pitfalls doc records (triggers shipped with no listener).
 * First consumer: the catalog list pages' Send To menu in the page header.
 */
;document.addEventListener('click', function (e) {
	'use strict';
	var trigger = e.target.closest ? e.target.closest('[data-am-menu-trigger]') : null;
	var anchor = trigger ? trigger.closest('.am-menu-anchor') : null;
	document.querySelectorAll('.am-menu-anchor.is-open').forEach(function (a) {
		if (a === anchor) return;
		// Opt-in for multi-select panels (DEV-12671): a menu holding checkboxes must survive
		// clicks on its own rows, or you reopen it for every option you tick. Single-select
		// menus are untouched — without the attribute the old close-everything rule stands.
		if (a.hasAttribute('data-am-menu-keep-open') && a.contains(e.target)) return;
		a.classList.remove('is-open');
	});
	if (anchor) {
		e.preventDefault();
		anchor.classList.toggle('is-open');
	}
});

// ---- Credential reveal + copy (DEV-12536) --------------------------------------------
// Delegated at parse time on document (same rationale as the tabs block above): the
// triggers are role=button spans inside .am-field--credential shells and .am-code-block
// parts. Contract:
//   [data-am-reveal]        — toggles the closest .am-field's input between password/text;
//                             state lives on aria-pressed (CSS swaps the eye glyphs).
//   [data-am-copy="#sel"]   — copies the referenced element's value/text (falls back to
//                             the closest field input or code block) via the async
//                             Clipboard API; feedback is a snackbar when available.
// Clicks preventDefault so a <label>-hosted control never forwards a synthetic click
// to the input (the datepicker/label-forwarding trap, DEV-12499 round 3).
(function () {
	'use strict';

	function copySource(trigger) {
		var sel = trigger.getAttribute('data-am-copy');
		if (sel) {
			var t = document.querySelector(sel);
			if (t) return ('value' in t && typeof t.value === 'string') ? t.value : t.textContent;
		}
		var field = trigger.closest('.am-field');
		if (field) {
			var inp = field.querySelector('.am-field__input');
			if (inp) return inp.value;
		}
		var code = trigger.closest('.am-code-block');
		if (code) {
			var clone = code.cloneNode(true);
			var btn = clone.querySelector('.am-code-block__copy');
			if (btn) btn.remove();
			return clone.textContent.trim();
		}
		return '';
	}

	function copiedFeedback() {
		if (typeof window.show_snackbar !== 'function') return;
		var msg = 'Copied';
		if (typeof window.get_language_translation === 'function') {
			var t = window.get_language_translation('LANG_MSG_COPIED_TO_CLIPBOARD');
			// get_language_translation returns the key itself on a miss — don't toast a raw key
			if (t && t !== 'LANG_MSG_COPIED_TO_CLIPBOARD') msg = t;
		}
		window.show_snackbar({ message: msg, timer: 2500 });
	}

	document.addEventListener('click', function (e) {
		var target = e.target instanceof Element ? e.target : null;
		if (!target) return;

		var reveal = target.closest('[data-am-reveal]');
		if (reveal) {
			e.preventDefault();
			var field = reveal.closest('.am-field');
			var inp = field && field.querySelector('.am-field__input');
			if (!inp) return;
			var show = inp.type === 'password';
			inp.type = show ? 'text' : 'password';
			reveal.setAttribute('aria-pressed', show ? 'true' : 'false');
			return;
		}

		var copy = target.closest('[data-am-copy]');
		if (copy) {
			e.preventDefault();
			var text = copySource(copy);
			if (!text) return;
			if (navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard.writeText(text).then(copiedFeedback, function () {});
			} else {
				// insecure-context fallback (navigator.clipboard needs https; local dev is http)
				var ta = document.createElement('textarea');
				ta.value = text;
				ta.style.position = 'fixed';
				ta.style.opacity = '0';
				document.body.appendChild(ta);
				ta.select();
				try { if (document.execCommand('copy')) copiedFeedback(); } catch (err) {}
				ta.remove();
			}
		}
	});

	// keyboard activation for the role=button spans (Enter/Space, per WAI button pattern)
	document.addEventListener('keydown', function (e) {
		if (e.key !== 'Enter' && e.key !== ' ') return;
		var target = e.target instanceof Element ? e.target : null;
		if (!target) return;
		if (target.matches('[data-am-reveal], [data-am-copy]')) {
			e.preventDefault();
			target.click();
		}
	});
})();

// ---- Docked action bar visibility (DEV-12536) ----------------------------------------
// Canon contract: the bar shows only while there are unsaved changes; app logic toggles
// it, not CSS. Both dirty systems on the converted settings pages (the shared
// set_input_state/check_dirty_inputs mechanism and extensions' hand-rolled
// toggle_save_state) converge on the save button's disabled attribute, so the bar
// watches that: <div class="am-action-bar" data-am-actionbar-for="save_changes" hidden>
// is visible exactly while #save_changes is enabled. A MutationObserver keeps it in
// sync no matter which mechanism flips the button.
(function () {
	'use strict';

	function wire(bar) {
		var btnId = bar.getAttribute('data-am-actionbar-for');
		var btn = btnId && document.getElementById(btnId);
		if (!btn) return;
		function sync() {
			if (btn.disabled) bar.setAttribute('hidden', '');
			else bar.removeAttribute('hidden');
		}
		new MutationObserver(sync).observe(btn, { attributes: true, attributeFilter: ['disabled'] });
		sync();
	}

	function init() {
		document.querySelectorAll('.am-action-bar[data-am-actionbar-for]').forEach(wire);
	}

	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
	else init();
})();
;document.addEventListener('click', function (e) {
	'use strict';
	if (!e.target.closest) return;

	var open = e.target.closest('[data-dialog-open]');
	if (open) {
		var host = document.getElementById(open.getAttribute('data-dialog-open'));
		if (host) host.classList.add('is-open');
		return;
	}

	var close = e.target.closest('[data-dialog-close]');
	if (close) {
		var owner = close.closest('.am-dialog-host, .am-sheet-host');
		if (owner) owner.classList.remove('is-open');
		return;
	}

	am_pick_dialog_section(e.target.closest('[data-section]'));
});

;document.addEventListener('keydown', function (e) {
	'use strict';
	if (e.key !== 'Enter' && e.key !== ' ') return;
	if (!e.target.closest) return;
	var row = e.target.closest('[data-section]');
	if (!row) return;
	e.preventDefault();
	am_pick_dialog_section(row);
});

function am_pick_dialog_section(row) {
	'use strict';
	if (!row) return;
	var scope = row.closest('[data-section-scope]');
	var name = row.getAttribute('data-section');
	if (!scope || !name) return;
	scope.querySelectorAll('[data-section]').forEach(function (i) {
		i.classList.toggle('am-list__item--selected', i === row);
	});
	scope.querySelectorAll('[data-section-panel]').forEach(function (p) {
		p.hidden = (p.getAttribute('data-section-panel') !== name);
	});
}

/**
 * Expansion panel headers (DEV-12317; Beta arrival via DEV-12542): a click on
 * .am-expand__header (or the slotted .am-expand__stretch) toggles .is-open on its
 * .am-expand and mirrors aria-expanded — m3/_expansion-panel.scss owns the grid-rows
 * reveal + chevron rotation. Parse-time delegated on document, so panels injected
 * after load (ajax sections) work with zero page wiring. Tooltip triggers inside a
 * header are excluded so an info hover never toggles the section.
 */
;document.addEventListener('click', function (e) {
	'use strict';
	if (!e.target.closest) return;
	if (e.target.closest('.am-toolTipContainer .fa-info-circle, .am-tooltip')) return;
	var header = e.target.closest('.am-expand__header, .am-expand__stretch');
	if (!header) return;
	var section = header.closest('.am-expand');
	if (!section || section.classList.contains('is-disabled')) return;
	var open = section.classList.toggle('is-open');
	header.setAttribute('aria-expanded', open ? 'true' : 'false');
});
// ---- Docked action bar: the scrolled-under state (canon G4b, ported DEV-12519) --------
// The bar separates from the page while content is actually passing beneath it — OR
// while anything sits below it in flow. Canon measures only scroll room ("flat at the
// very bottom / on a page too short to scroll"), which assumes the bar is the last
// thing in the scroller; in MRP the copyright footer always renders after the bar, and
// on a short page sticky-bottom leaves the bar mid-viewport — a flat white strip
// floating above the footer read as broken (DEV-12524 QA, adjustments/new). So the
// flow-below check is an MRP addendum to the canon measurement (flagged as a gap).
// m3/_action-bar.scss owns the look. Keyed on the part's own class so every docked bar
// on every page gets it with no page-level wiring. The scroller is whichever ancestor
// actually scrolls (#content_column on an app page, the document on a bare one), found
// by measurement rather than by class name.
(function () {
	'use strict';

	function init() {
		var bars = document.querySelectorAll('.am-action-bar');
		if (!bars.length) return;
		var scrollerOf = function (el) {
			var n = el.parentElement;
			while (n && n !== document.body) {
				var oy = getComputedStyle(n).overflowY;
				if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight + 1) return n;
				n = n.parentElement;
			}
			return document.scrollingElement || document.documentElement;
		};
		bars.forEach(function (bar) {
			var sc = scrollerOf(bar);
			var sync = function () {
				var room = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
				// content below the bar in FLOW (the page footer under a bar that sits at
				// its natural position) also counts as "beneath it"
				var scTop = (sc === document.scrollingElement || sc === document.documentElement)
					? 0 : sc.getBoundingClientRect().top;
				var barBottomInContent = bar.getBoundingClientRect().bottom - scTop + sc.scrollTop;
				var flowBelow = sc.scrollHeight - barBottomInContent;
				bar.classList.toggle('is-scrolled-under', room > 1 || flowBelow > 1);
			};
			sc.addEventListener('scroll', sync, { passive: true });
			window.addEventListener('resize', sync);
			if (typeof ResizeObserver === 'function') new ResizeObserver(sync).observe(bar);
			sync();
		});
	}

	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
	else init();
})();

// ---- Tab-with-menu: opens on CLICK, never hover (canon tabs.css gap 58 ruling,
// 2026-08-15/16). Tabset.php emits <li class="tab_menu"><div>Label</div><ul>…</ul></li>;
// m3/_tabs.scss keys the panel reveal and caret rotation on .tab_menu.is-open. Delegated
// document-level handlers (canon: tabs answer clicks no matter when they were born), so
// a strip injected late is wired too. The trigger div is progressively enhanced into a
// button (tabindex/role/aria-haspopup/aria-expanded) at parse and again on first touch.
;(function () {
	'use strict';

	function triggerOf(li) { return li.querySelector(':scope > div'); }

	function setOpen(li, open) {
		li.classList.toggle('is-open', open);
		var d = triggerOf(li);
		if (d) d.setAttribute('aria-expanded', open ? 'true' : 'false');
	}

	function closeAll(except) {
		document.querySelectorAll('.am-tabs .tab_menu.is-open').forEach(function (li) {
			if (li !== except) setOpen(li, false);
		});
	}

	function enhance(li) {
		var d = triggerOf(li);
		if (!d || d.hasAttribute('role')) return;
		d.setAttribute('tabindex', '0');
		d.setAttribute('role', 'button');
		d.setAttribute('aria-haspopup', 'true');
		d.setAttribute('aria-expanded', li.classList.contains('is-open') ? 'true' : 'false');
	}

	/* upward flip for the panel, same measure as the .am-menu auto-flip below (canon
	   behaviors.js, gap menu--upward-auto-flip option B): flip only when the panel
	   won't fit below AND there is more room above; a panel that fits nowhere stays
	   downward, the lesser evil. The panel keeps layout while hidden (visibility, not
	   display — _tabs.scss), so offsetHeight is measurable before it shows. */
	function flipPanel(li) {
		var panel = li.querySelector(':scope > ul');
		if (!panel) return;
		panel.classList.remove('am-menu--up');
		var r = li.getBoundingClientRect();
		var vh = window.innerHeight || document.documentElement.clientHeight;
		var below = vh - r.bottom;
		if (panel.offsetHeight > below && r.top > below) panel.classList.add('am-menu--up');
	}

	document.addEventListener('click', function (e) {
		if (!e.target.closest) return;
		var d = e.target.closest('.am-tabs .tab_menu > div');
		if (d) {
			var li = d.parentNode;
			enhance(li);
			var willOpen = !li.classList.contains('is-open');
			closeAll(li);
			if (willOpen) flipPanel(li);
			setOpen(li, willOpen);
			return;
		}
		/* a pick inside the panel navigates (the items are link tabs) — close so the
		   menu isn't left hanging open behind an ajax-driven page */
		if (e.target.closest('.am-tabs .tab_menu > ul')) { closeAll(null); return; }
		closeAll(null); /* outside click closes */
	});

	document.addEventListener('keydown', function (e) {
		if (!e.target || !e.target.closest) return;
		if (e.key === 'Escape') {
			var open = document.querySelector('.am-tabs .tab_menu.is-open');
			if (open) {
				setOpen(open, false);
				var d = triggerOf(open);
				if (d) d.focus(); /* restore focus to the trigger, per the menu pattern */
			}
			return;
		}
		if (e.key !== 'Enter' && e.key !== ' ') return;
		var trig = e.target.closest('.am-tabs .tab_menu > div');
		if (trig && e.target === trig) {
			e.preventDefault();
			trig.click(); /* Enter/Space activate the role=button trigger */
		}
	});

	function init() {
		document.querySelectorAll('.am-tabs .tab_menu').forEach(enhance);
	}
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
	else init();
})();

// ---- Menu upward auto-flip (canon behaviors.js, gap menu--upward-auto-flip, option B,
// ruled 2026-08-15): decide direction at OPEN time by measuring the space under the
// anchor, because anchor position depends on scroll and a build-time class cannot know
// it. Flip up (.am-menu--up on the panel, m3/_menu.scss) only when the menu will not
// fit below AND there is more room above; a menu that fits nowhere stays downward, the
// lesser evil. Recomputed every open. The app has three openers that all converge on
// .am-menu-anchor gaining .is-open ([data-am-menu-trigger] above, activate_options_menu
// in generic_helpers.js, the livetable popup-holder in event_delegation.js), so the
// hook is a class-attribute MutationObserver rather than three copies of the measure:
// whichever opener toggles the class, the flip runs before the next paint. The .am-menu
// panel keeps layout while hidden (visibility, not display), so its height is
// measurable before it shows. With JS off nothing opens these menus, so no flip is owed.
;(function () {
	'use strict';
	if (!window.MutationObserver) return;

	function flip(anchor) {
		var r = anchor.getBoundingClientRect();
		var vh = window.innerHeight || document.documentElement.clientHeight;
		var below = vh - r.bottom;
		var above = r.top;
		anchor.querySelectorAll('.am-menu').forEach(function (m) {
			m.classList.remove('am-menu--up');
			if (m.offsetHeight > below && above > below) m.classList.add('am-menu--up');
		});
	}

	function init() {
		new MutationObserver(function (mutations) {
			mutations.forEach(function (m) {
				var el = m.target;
				if (el.nodeType !== 1 || !el.classList) return;
				/* re-measure while open; leave --up alone on close (canon clears it at the
				   NEXT open, so the close transition doesn't jump the panel downward) */
				if (el.classList.contains('am-menu-anchor') && el.classList.contains('is-open')) flip(el);
			});
		}).observe(document.body, { attributes: true, attributeFilter: ['class'], subtree: true });
	}
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
	else init();
})();

// ---- Date picker (canon date-picker.css + the date-picker section of canon behaviors.js,
// ported DEV-12516 wave 1; first consumer: the invoices rail Invoice Date From/To) --------
// Anatomy (showroom markup): .am-datepicker-anchor > [data-datepicker-trigger].am-datepicker__field
// + .am-datepicker-panel > .am-datepicker (single; .am-datepicker--range for a span). MRP
// contract on top of canon: an anchor may carry a hidden <input data-datepicker-input>. That
// input is the single source of truth — it holds the ISO date (YYYY-MM-DD), the calendar
// renders FROM it on every open (so a programmatic reset, e.g. livetable's lt_clear_filters,
// is honest on the next open), a pick writes it and dispatches a native `change` event (the
// page's hook), and the field's value span mirrors it (window.amDatepickerSync(input) re-mirrors
// after an external write). Display text is localized through Intl (canon hardcodes English
// month names; MRP serves seven languages), format = canon fmtField ("Sep 8, 2026").
// Parse-time delegated (the house convention above; canon binds per element at ready) so
// late-rendered rails need no wiring. Escape / outside click close; the generic menu closer
// above does not know this anchor, so closing lives here.
;(function () {
	'use strict';
	var LANG = function () { return document.documentElement.lang || 'en'; };
	var NAV_SVG = '<button type="button" data-nav="-1" aria-label="Previous month"><span class="am-icon" data-icon="chevron_left" aria-hidden="true"></span></button>'
		+ '<button type="button" data-nav="1" aria-label="Next month"><span class="am-icon" data-icon="chevron_right" aria-hidden="true"></span></button>';
	var CARET_SVG = '<span class="am-icon" data-icon="arrow_drop_down" aria-hidden="true"></span>';
	var YNAV_SVG = '<button type="button" data-navy="-1" aria-label="Earlier years"><span class="am-icon" data-icon="chevron_left" aria-hidden="true"></span></button>'
		+ '<button type="button" data-navy="1" aria-label="Later years"><span class="am-icon" data-icon="chevron_right" aria-hidden="true"></span></button>';

	function parseISO(s) {
		if (!s) return null;
		var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s).trim());
		return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
	}
	function iso(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }
	function fmtField(d) { return d.toLocaleDateString(LANG(), { month: 'short', day: 'numeric', year: 'numeric' }); }
	function fmtMonth(d) { return d.toLocaleDateString(LANG(), { month: 'long', year: 'numeric' }); }
	function weekdays() {
		var out = [], base = new Date(2024, 0, 7); /* a Sunday */
		for (var i = 0; i < 7; i++) { var d = new Date(base); d.setDate(base.getDate() + i); out.push(d.toLocaleDateString(LANG(), { weekday: 'narrow' })); }
		return out;
	}
	function sameDay(a, b) { return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

	function inputOf(anchor) { return anchor.querySelector('input[data-datepicker-input]'); }
	/* mirror the hidden input onto the field's value span (placeholder when empty) */
	function syncFromInput(input) {
		var anchor = input.closest('.am-datepicker-anchor'); if (!anchor) return;
		var v = anchor.querySelector('.am-datepicker__field-value'); if (!v) return;
		var d = parseISO(input.value);
		if (d) { v.textContent = fmtField(d); v.classList.remove('is-placeholder'); }
		else { v.textContent = v.getAttribute('data-placeholder') || ''; v.classList.add('is-placeholder'); }
	}
	window.amDatepickerSync = syncFromInput;

	function monthBtn(labelText) {
		return '<button type="button" class="am-datepicker__month am-datepicker__month--btn" data-toggle-years aria-label="Choose year">' + labelText + CARET_SVG + '</button>';
	}
	function yearViewHTML(base, curYear) {
		var h = '<div class="am-datepicker__head"><button type="button" class="am-datepicker__month am-datepicker__month--btn is-years" data-toggle-years aria-label="Back to calendar">'
			+ base + ' – ' + (base + 11) + CARET_SVG + '</button><div class="am-datepicker__nav">' + YNAV_SVG + '</div></div>'
			+ '<div class="am-datepicker__years">';
		for (var i = 0; i < 12; i++) { var yr = base + i; h += '<button type="button" class="am-datepicker__year' + (yr === curYear ? ' is-selected' : '') + '" data-year="' + yr + '">' + yr + '</button>'; }
		return h + '</div>';
	}

	/* single-date calendar rendered into el, seeded from the anchor's input (or data-selected) */
	function initSinglePicker(el) {
		var today = new Date();
		var anchor = el.closest('.am-datepicker-anchor');
		var input = anchor ? inputOf(anchor) : null;
		var sel = parseISO(input ? input.value : el.dataset.selected) || (anchor ? null : new Date(today.getFullYear(), today.getMonth(), today.getDate()));
		var view = new Date((sel || today).getFullYear(), (sel || today).getMonth(), 1);
		var mode = 'days', yearBase = view.getFullYear() - 5;
		var WD = weekdays();
		function wireYears() {
			el.querySelector('[data-toggle-years]').onclick = function () { mode = 'days'; render(); };
			el.querySelectorAll('[data-navy]').forEach(function (b) { b.onclick = function () { yearBase += 12 * parseInt(b.dataset.navy, 10); render(); }; });
			el.querySelectorAll('[data-year]').forEach(function (b) { b.onclick = function () { view.setFullYear(parseInt(b.dataset.year, 10)); mode = 'days'; render(); }; });
		}
		function render() {
			var y = view.getFullYear(), m = view.getMonth();
			if (mode === 'years') { el.innerHTML = yearViewHTML(yearBase, y); wireYears(); return; }
			var first = new Date(y, m, 1).getDay(), days = new Date(y, m + 1, 0).getDate();
			var h = '<div class="am-datepicker__head">' + monthBtn(fmtMonth(view)) + '<div class="am-datepicker__nav">' + NAV_SVG + '</div></div>'
				+ '<div class="am-datepicker__weekdays">' + WD.map(function (d) { return '<div class="am-datepicker__wd">' + d + '</div>'; }).join('') + '</div><div class="am-datepicker__grid">';
			for (var i = 0; i < first; i++) h += '<div class="am-datepicker__day"><button type="button" class="is-empty" tabindex="-1"></button></div>';
			for (var d = 1; d <= days; d++) {
				var isT = (y === today.getFullYear() && m === today.getMonth() && d === today.getDate());
				var isS = sel && (y === sel.getFullYear() && m === sel.getMonth() && d === sel.getDate());
				h += '<div class="am-datepicker__day"><button type="button" data-day="' + d + '" class="' + (isT ? 'is-today ' : '') + (isS ? 'is-selected' : '') + '">' + d + '</button></div>';
			}
			el.innerHTML = h + '</div>';
			el.querySelector('[data-toggle-years]').onclick = function () { yearBase = view.getFullYear() - 5; mode = 'years'; render(); };
			el.querySelectorAll('[data-nav]').forEach(function (b) { b.onclick = function () { view.setMonth(view.getMonth() + parseInt(b.dataset.nav, 10)); render(); }; });
			el.querySelectorAll('[data-day]').forEach(function (b) {
				b.onclick = function () {
					sel = new Date(view.getFullYear(), view.getMonth(), parseInt(b.dataset.day, 10));
					render();
					if (input) {
						input.value = iso(sel);
						syncFromInput(input);
						input.dispatchEvent(new Event('change', { bubbles: true }));
					} else if (anchor) {
						var v = anchor.querySelector('.am-datepicker__field-value');
						if (v) { v.textContent = fmtField(sel); v.classList.remove('is-placeholder'); }
					}
					if (anchor) { closeAll(null); var t = anchor.querySelector('[data-datepicker-trigger]'); if (t) t.focus(); }
				};
			});
		}
		render();
	}

	/* range calendar (canon initRangePicker; seeds from data-start/data-end, closes when the end lands) */
	function initRangePicker(el) {
		var today = new Date();
		var start = parseISO(el.dataset.start), end = parseISO(el.dataset.end);
		var anchor = el.closest('.am-datepicker-anchor');
		if (!start && !end && !anchor) { start = new Date(today.getFullYear(), today.getMonth(), 8); end = new Date(today.getFullYear(), today.getMonth(), 19); }
		var view = new Date((start || today).getFullYear(), (start || today).getMonth(), 1);
		var mode = 'days', yearBase = view.getFullYear() - 5;
		var WD = weekdays();
		function fmtShort(d) { return d.toLocaleDateString(LANG(), { month: 'short', day: 'numeric' }); }
		function sync(text) { if (!anchor) return; var v = anchor.querySelector('.am-datepicker__field-value'); if (v) { v.textContent = text; v.classList.remove('is-placeholder'); } }
		function render() {
			var y = view.getFullYear(), m = view.getMonth();
			if (mode === 'years') {
				el.innerHTML = yearViewHTML(yearBase, y);
				el.querySelector('[data-toggle-years]').onclick = function () { mode = 'days'; render(); };
				el.querySelectorAll('[data-navy]').forEach(function (b) { b.onclick = function () { yearBase += 12 * parseInt(b.dataset.navy, 10); render(); }; });
				el.querySelectorAll('[data-year]').forEach(function (b) { b.onclick = function () { view.setFullYear(parseInt(b.dataset.year, 10)); mode = 'days'; render(); }; });
				return;
			}
			var first = new Date(y, m, 1).getDay(), days = new Date(y, m + 1, 0).getDate();
			var txt = (start && end) ? (fmtShort(start) + ' – ' + fmtShort(end)) : (start ? fmtShort(start) + ' – …' : '');
			var h = '<div class="am-datepicker__rangehead"><strong>' + txt + '</strong></div>'
				+ '<div class="am-datepicker__head">' + monthBtn(fmtMonth(view)) + '<div class="am-datepicker__nav">' + NAV_SVG + '</div></div>'
				+ '<div class="am-datepicker__weekdays">' + WD.map(function (d) { return '<div class="am-datepicker__wd">' + d + '</div>'; }).join('') + '</div><div class="am-datepicker__grid">';
			for (var i = 0; i < first; i++) h += '<div class="am-datepicker__day"><button type="button" class="is-empty" tabindex="-1"></button></div>';
			for (var d = 1; d <= days; d++) {
				var cur = new Date(y, m, d), cls = [];
				if (sameDay(cur, start)) cls.push('is-start');
				if (sameDay(cur, end)) cls.push('is-end');
				if (start && end && cur > start && cur < end) cls.push('is-in-range');
				h += '<div class="am-datepicker__day ' + cls.join(' ') + '"><button type="button" data-day="' + d + '">' + d + '</button></div>';
			}
			el.innerHTML = h + '</div>';
			el.querySelector('[data-toggle-years]').onclick = function () { yearBase = view.getFullYear() - 5; mode = 'years'; render(); };
			el.querySelectorAll('[data-nav]').forEach(function (b) { b.onclick = function () { view.setMonth(view.getMonth() + parseInt(b.dataset.nav, 10)); render(); }; });
			el.querySelectorAll('[data-day]').forEach(function (b) {
				b.onclick = function () {
					var d = new Date(view.getFullYear(), view.getMonth(), parseInt(b.dataset.day, 10));
					if (!start || (start && end)) { start = d; end = null; }
					else if (d >= start) { end = d; }
					else { start = d; }
					render();
					if (start && end) { sync(fmtField(start) + ' – ' + fmtField(end)); if (anchor) anchor.classList.remove('is-open'); }
					else if (start) sync(fmtField(start) + ' –');
				};
			});
		}
		render();
	}

	/* Panel placement. Canon hangs the panel absolutely under the field (the rail it was
	   drawn for has no overflow clipping). The legacy sidebar shells (#rightside,
	   #sidebar_collapse, #page_wrapper) are all overflow:hidden, so inside them the 328px
	   calendar was cut off at the 280px rail edge; there m3/_date-picker.scss makes the
	   panel position:fixed and this places it from the trigger's viewport rect — the same
	   viewport-fixed answer the tooltip took above — clamped to the viewport (flips above
	   the field when there is no room below) and closed on any scroll, since a fixed panel
	   cannot follow its field. Absolute (canon) panels are left alone. */
	function placePanel(anchor) {
		var panel = anchor.querySelector('.am-datepicker-panel'), trigger = anchor.querySelector('[data-datepicker-trigger]');
		if (!panel || !trigger || getComputedStyle(panel).position !== 'fixed') return;
		var tr = trigger.getBoundingClientRect(), pw = panel.offsetWidth, ph = panel.offsetHeight;
		var vw = window.innerWidth || document.documentElement.clientWidth, vh = window.innerHeight || document.documentElement.clientHeight;
		var left = Math.max(8, Math.min(tr.left, vw - pw - 8));
		var top = tr.bottom + 4;
		if (top + ph > vh - 8 && tr.top - ph - 4 >= 8) top = tr.top - ph - 4;
		panel.style.left = left + 'px'; panel.style.top = top + 'px';
	}
	function closeAll(except) {
		document.querySelectorAll('.am-datepicker-anchor.is-open').forEach(function (a) { if (a !== except) a.classList.remove('is-open'); });
	}
	function openAnchor(anchor) {
		closeAll(anchor);
		var cal = anchor.querySelector('.am-datepicker');
		if (cal) { /* re-render from the input on every open: the input is the truth */
			if (cal.classList.contains('am-datepicker--range')) { if (!cal.children.length) initRangePicker(cal); }
			else initSinglePicker(cal);
		}
		anchor.classList.add('is-open');
		placePanel(anchor);
	}
	document.addEventListener('scroll', function () { if (document.querySelector('.am-datepicker-anchor.is-open .am-datepicker-panel')) closeAll(null); }, true);
	window.addEventListener('resize', function () { document.querySelectorAll('.am-datepicker-anchor.is-open').forEach(placePanel); });

	document.addEventListener('click', function (e) {
		if (!e.target.closest) return;
		var trigger = e.target.closest('[data-datepicker-trigger]');
		if (trigger) {
			var anchor = trigger.closest('.am-datepicker-anchor');
			if (!anchor) return;
			e.preventDefault();
			if (anchor.classList.contains('is-open')) anchor.classList.remove('is-open'); else openAnchor(anchor);
			return;
		}
		/* clicks inside the calendar stay open. Read the dispatch path, not the target's
		   ancestors: a day/month/year button's own handler re-renders the calendar's innerHTML
		   before this listener runs, so the target is already detached and closest() would
		   miss the panel (the year view closed on its first click before this guard). */
		var path = e.composedPath ? e.composedPath() : [];
		for (var i = 0; i < path.length; i++) { if (path[i].classList && path[i].classList.contains('am-datepicker-panel')) return; }
		closeAll(null);
	});
	document.addEventListener('keydown', function (e) {
		if (e.key !== 'Escape') return;
		document.querySelectorAll('.am-datepicker-anchor.is-open').forEach(function (a) {
			var t = a.querySelector('[data-datepicker-trigger]'); if (t) t.focus();
		});
		closeAll(null);
	});

	/* standalone (un-anchored) calendars render at ready, as in canon; anchored fields
	   mirror their input so the server-rendered text and the localized text agree */
	function init() {
		document.querySelectorAll('.am-datepicker').forEach(function (el) {
			if (el.closest('.am-datepicker-anchor') || el.children.length) return;
			if (el.classList.contains('am-datepicker--range')) initRangePicker(el); else initSinglePicker(el);
		});
		document.querySelectorAll('.am-datepicker-anchor input[data-datepicker-input]').forEach(syncFromInput);
	}
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
